/**
 * Tool Items Context
 *
 * Provides a way to pass shaped items from the workspace into prioritization tools.
 * For hooks (useToolItems, useToolMode, useShapedItemsForTools) and withToolItems,
 * import from ./toolItemsHooks to keep this file component-only for Fast Refresh.
 */

import { createContext, useState, useCallback, type ReactNode } from 'react';
import { dedupeById } from '../lib/dedupeById';
import type { OutputForm } from '../lib/methodRegistry';
import type { DecisionClass } from '../types/prioritization/session';
import type { EffortAttribute, EffortReading } from '../lib/buyAFeaturePricing';

// Generic tool item interface that tools can map from
export interface ToolItem {
  id: string;
  name: string;
  description?: string;
  attributes?: Record<string, unknown>;
  sourceId?: string;
  sourceType?: 'idea' | 'item' | 'priority';
}

export interface ToolResult {
  itemId: string;
  rank: number;
  score?: number;
  category?: string;
  metadata?: Record<string, unknown>;
}

export type ToolHostContext = 'workspace' | 'session' | 'standalone' | 'task';

/**
 * Controls whether and how the tool receives items:
 *
 * preloaded  — items are already injected; suppress all import/create UI entirely
 * workspace  — items come from workspace shaped items; show "Import from Workspace" button
 * manual     — no items available; show "Add items" zero state (true standalone)
 */
export type ToolInputMode = 'preloaded' | 'workspace' | 'manual';

/**
 * What happens when the user clicks the primary completion action.
 * Drives the button label and the post-completion behavior.
 */
export interface ToolCompletionConfig {
  /** Label for the primary completion button */
  label: string;
  /** Human-readable description of where results go, shown near the button */
  destinationHint?: string;
  /** The output form this tool is expected to produce */
  outputForm?: OutputForm;
  /** The decision class this tool run is serving */
  decisionClass?: DecisionClass;
  /** Whether to navigate away on completion (true for session runs) */
  navigateOnComplete?: boolean;
}

/**
 * Full situational context passed into a tool so it knows exactly where it
 * sits in the workflow — what's coming in and what happens after.
 */
/**
 * What a method screen otherwise reads from the session row. Set only for a
 * guest, who cannot read the session: the guest room receives it from
 * `get_guest_activity` (migration 964) and passes it down.
 */
export interface SessionReadout {
  criteriaConfig?: { criteriaIds?: string[]; weights?: Record<string, number> } | null;
  /** The session engine's criteria (mode_config.v3Config.criteria). */
  engineCriteria?: Array<{ id: string; name?: string; description?: string; weight?: number }>;
  /** Label and description of each declared criterion, from the workspace's attributes. */
  criterionLabels?: Record<string, { label?: string; description?: string }>;
  /** Each workspace item's official effort, keyed by item id. */
  itemEfforts?: Record<string, EffortReading>;
  effortAttribute?: EffortAttribute | null;
}

export interface ToolSituationalContext {
  inputMode: ToolInputMode;
  completion: ToolCompletionConfig;
  /** Session ID if running inside a session */
  sessionId?: string;
  /** Cycle ID if running in a cycle context */
  cycleId?: string;
  /** Track ID if running in a track context */
  trackId?: string;
  /** Participants in the session, for collaborative tool runs */
  sessionParticipants?: { id: string; name: string }[];
  /** ID of the current user — used to attribute their submission */
  currentUserId?: string;
  /** Display name of the current user */
  currentUserName?: string;
  /** Whether the current user is the session facilitator */
  isFacilitator?: boolean;
  /** If a ToolSession is already active for this tool+session, its ID */
  activeToolSessionId?: string;
  /** Workspace ID — required for persisting tool session responses to the DB */
  workspaceId?: string;
  /** Facilitator's user ID — passed to FacilitatorControlPanel */
  facilitatorId?: string;
  /** Facilitator's display name — passed to FacilitatorControlPanel */
  facilitatorName?: string;
  /**
   * The session's tool settings (prioritization_sessions.tool_config), as
   * saved. Tools read them through `useToolSettings()` — never directly — so
   * old camelCase rows and defaults are handled in one place.
   */
  toolConfig?: Record<string, unknown>;
  /** A guest's copy of what the screen would read from the session row. */
  sessionReadout?: SessionReadout;
  /** Online users in the session from presenceStore */
  presentUsers?: import('../stores/prioritization/presenceStore').PresentUser[];
}

interface ToolItemsContextType {
  items: ToolItem[];
  hasPreloadedItems: boolean;
  hostContext?: ToolHostContext;
  /** Rich situational context — supersedes coarse hostContext for UI decisions */
  situationalContext?: ToolSituationalContext;
  results: ToolResult[];
  setResults: (results: ToolResult[]) => void;
  saveResultsToWorkspace: () => Promise<void>;
  clearItems: () => void;
}

// eslint-disable-next-line react-refresh/only-export-components -- context paired with its provider
export const ToolItemsContext = createContext<ToolItemsContextType | null>(null);

interface ToolItemsProviderProps {
  children: ReactNode;
  items?: ToolItem[];
  hostContext?: ToolHostContext;
  /** When provided, overrides all coarse hostContext-based UI decisions */
  situationalContext?: ToolSituationalContext;
}

/**
 * Provider component for tool items context
 */
export function ToolItemsProvider({
  children,
  items: initialItems,
  hostContext,
  situationalContext,
}: ToolItemsProviderProps) {
  // A tool tells its items apart by id, and a typed option's id is its text. Two
  // items sharing one id act as one: a dot placed on one lands on all of them
  // (roadmap N-6). Rows saved before setIsolatedItems kept lines distinct can
  // still hold repeats, so every tool, guest or member, gets each id once.
  const [items] = useState<ToolItem[]>(() => dedupeById(initialItems ?? []));
  const [results, setResults] = useState<ToolResult[]>([]);

  const saveResultsToWorkspace = useCallback(async () => {
    console.log('Saving tool results to workspace:', results);
  }, [results]);

  const clearItems = useCallback(() => {
    setResults([]);
  }, []);

  return (
    <ToolItemsContext.Provider
      value={{
        items,
        hasPreloadedItems: items.length > 0,
        hostContext,
        situationalContext,
        results,
        setResults,
        saveResultsToWorkspace,
        clearItems,
      }}
    >
      {children}
    </ToolItemsContext.Provider>
  );
}
